import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import { api } from '../../../shared/services/API'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import { ESTADO_TONE, PLANO_LABEL, formatDate, type TenantAdminListItem } from '../tenantModel'

export default function TenantsList() {
    const navigate = useNavigate()

    const [rows, setRows] = useState<TenantAdminListItem[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-tenants:table-page', 20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
        return () => window.clearTimeout(id)
    }, [search])

    useEffect(() => {
        let cancelled = false
        setIsLoading(true)
        setError('')
        api.get<Page<TenantAdminListItem>>('/api/admin/tenants', {
            params: {
                page,
                size: rowsPerPage,
                sort: 'nome,asc',
                ...(debouncedSearch ? { q: debouncedSearch } : {}),
            },
        })
            .then((r) => {
                if (cancelled) return
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => { if (!cancelled) setError('Não foi possível carregar a lista de empresas.') })
            .finally(() => { if (!cancelled) setIsLoading(false) })
        return () => { cancelled = true }
    }, [page, rowsPerPage, debouncedSearch])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    const emptyMessage = useMemo(
        () => (debouncedSearch ? 'Nenhuma empresa corresponde à pesquisa.' : 'Ainda não há empresas registadas.'),
        [debouncedSearch],
    )

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Empresas</Typography>
                <Typography variant="body2" color="text.secondary">
                    {total} {total === 1 ? 'empresa registada' : 'empresas registadas'}
                </Typography>
            </Box>

            <TextField
                size="small"
                placeholder="Pesquisar por nome, email ou schema…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
            />

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Schema</TableCell>
                                <TableCell>Plano</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Email confirmado</TableCell>
                                <TableCell>Criado em</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((t) => (
                                    <TableRow
                                        key={t.id}
                                        hover
                                        onClick={() => navigate(`/tenants/${t.id}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ fontWeight: 600 }}>{t.nome}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{t.email || '—'}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {t.schemaName}
                                        </TableCell>
                                        <TableCell>{PLANO_LABEL[t.plano] ?? t.plano}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={ESTADO_TONE[t.estado]?.label ?? t.estado}
                                                sx={{
                                                    bgcolor: ESTADO_TONE[t.estado]?.bg ?? '#F3F4F6',
                                                    color: ESTADO_TONE[t.estado]?.color ?? '#374151',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{t.emailConfirmado ? 'Sim' : 'Não'}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(t.criadoEm)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>
        </Box>
    )
}
